import type { DeliverableStatus } from "@dream/config/constants";
import { IconGoogle, LateBadge, PublishBadge, RecurringBadge, StatusBadge } from "@dream/ui";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DreamEventProps, GoogleEventProps } from "../calendar/adapter/mapping.ts";
import { chipInkForFill } from "../lib/chip-contrast.ts";
import { TypeIcon } from "./type-icon.tsx";

/**
 * Resumo no hover do calendário.
 *
 * O briefing pede um cartão curto e bonito ao passar o mouse — sem abrir o
 * diálogo. O portal no `document.body` evita que a célula da grade recorte o
 * cartão. `pointer-events: none` garante que o resumo não intercepte o clique.
 */

export interface EventHoverModel {
  title: string;
  typeLabel: string | null;
  typeSlug: string | null;
  typeIcon: string | null;
  color: string;
  clientName: string | null;
  projectName: string | null;
  status: DeliverableStatus;
  isLate: boolean;
  isRecurring: boolean;
  visibleToClient: boolean;
  hasPublish: boolean;
  description: string | null;
  when: string;
  rect: { top: number; left: number; width: number; height: number };
  kind?: "deliverable" | "google";
  location?: string | null;
  hangoutLink?: string | null;
  attendees?: string[];
}

const CARD_WIDTH = 240;

export function hoverModelFromProps(input: {
  title: string;
  props: DreamEventProps;
  color: string;
  allDay: boolean;
  timeText: string;
  el: HTMLElement;
}): EventHoverModel {
  const { title, props, color, allDay, timeText, el } = input;
  const rect = el.getBoundingClientRect();
  const day = DateTime.fromISO(props.occurrenceStart, { zone: props.timezone })
    .setLocale("pt-BR")
    .toFormat("ccc, dd LLL");
  const when = allDay
    ? `${day} · Dia inteiro`
    : `${day} · ${timeText.replace(/^0/, "").replace(/\s?-\s?/, "–")}`;

  return {
    title,
    typeLabel: props.typeLabel,
    typeSlug: props.typeSlug,
    typeIcon: props.typeIcon,
    color,
    clientName: props.clientName,
    projectName: props.projectName,
    status: props.status,
    isLate: props.isLate,
    isRecurring: props.isRecurring,
    visibleToClient: props.visibleToClient,
    hasPublish: props.hasPublish,
    description: props.description,
    when,
    rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
  };
}

export function googleHoverModelFromEvent(input: {
  title: string;
  props: GoogleEventProps;
  color: string;
  allDay: boolean;
  timeText: string;
  el: HTMLElement;
}): EventHoverModel {
  const { title, props, color, allDay, timeText, el } = input;
  const rect = el.getBoundingClientRect();

  return {
    title,
    typeLabel: props.calendarName,
    typeSlug: "meeting",
    typeIcon: "meeting",
    color,
    clientName: props.location !== null && props.location !== "" ? props.location : null,
    projectName: null,
    status: "planned",
    isLate: false,
    isRecurring: false,
    visibleToClient: false,
    hasPublish: false,
    description: "Evento da Agenda da equipe. O cliente não vê.",
    when: allDay ? "Dia inteiro" : timeText,
    rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
    kind: "google",
    location: props.location,
    hangoutLink: props.hangoutLink,
    attendees: props.attendees,
  };
}

export function useEventHover() {
  const [model, setModel] = useState<EventHoverModel | null>(null);
  const [leaving, setLeaving] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((next: EventHoverModel) => {
    if (typeof window !== "undefined" && window.matchMedia("(hover: none)").matches) {
      return;
    }
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    setLeaving(false);
    setModel(next);
  }, []);

  const hide = useCallback(() => {
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setLeaving(true);
      hideTimer.current = setTimeout(() => {
        setModel(null);
        setLeaving(false);
      }, 160);
    }, 80);
  }, []);

  const clear = useCallback(() => {
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    setLeaving(false);
    setModel(null);
  }, []);

  useEffect(
    () => () => {
      if (hideTimer.current !== null) clearTimeout(hideTimer.current);
    },
    [],
  );

  return { model, leaving, show, hide, clear };
}

export function EventHoverCard({
  model,
  leaving = false,
}: {
  model: EventHoverModel | null;
  leaving?: boolean;
}) {
  if (model === null || typeof document === "undefined") return null;

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const cardWidth = Math.min(CARD_WIDTH, Math.max(196, viewportW - 16));
  const isGoogle = model.kind === "google";
  const estimatedHeight = isGoogle ? 188 : 148;
  const gap = 8;
  const spaceRight = viewportW - (model.rect.left + model.rect.width);
  const spaceLeft = model.rect.left;

  let top = model.rect.top;
  let left: number;

  if (spaceRight >= cardWidth + gap) {
    left = model.rect.left + model.rect.width + gap;
  } else if (spaceLeft >= cardWidth + gap) {
    left = model.rect.left - cardWidth - gap;
  } else {
    left = Math.max(
      8,
      Math.min(model.rect.left + model.rect.width / 2 - cardWidth / 2, viewportW - cardWidth - 8),
    );
    const preferBelow = model.rect.top < estimatedHeight + 16;
    top = preferBelow
      ? model.rect.top + model.rect.height + gap
      : model.rect.top - estimatedHeight - gap;
  }

  top = Math.max(8, Math.min(top, viewportH - estimatedHeight - 8));
  left = Math.max(8, Math.min(left, viewportW - cardWidth - 8));

  const snippet =
    model.description === null || model.description.trim() === ""
      ? null
      : model.description.trim().replace(/\s+/g, " ").slice(0, 140);

  return createPortal(
    <div
      data-testid="event-hover-card"
      role="tooltip"
      className={`pointer-events-none fixed z-[90] overflow-hidden rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] shadow-[0_16px_40px_rgb(0_0_0/0.14)] motion-reduce:animate-none ${leaving ? "dream-hover-leave" : "dream-hover-enter"}`}
      style={{ top, left, width: cardWidth }}
    >
      <div className="h-1 w-full" style={{ backgroundColor: model.color }} aria-hidden="true" />
      <div className="px-3 pb-3 pt-2.5">
        <div className="flex items-start gap-2.5">
          <span
            className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: model.color, color: chipInkForFill(model.color).ink }}
            aria-hidden="true"
          >
            {isGoogle ? (
              <IconGoogle className="size-4" />
            ) : (
              <TypeIcon slug={model.typeSlug} name={model.typeIcon} size={16} />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-[13px] font-bold leading-5 tracking-[0] text-[var(--color-ink)]">
              {model.title}
            </p>
            {model.typeLabel !== null && (
              <p className="mt-0.5 text-[11px] font-medium text-[var(--color-ink-muted)]">
                {model.typeLabel}
              </p>
            )}
          </div>
        </div>

        <p className="mt-2.5 text-[12px] font-medium tabular-nums text-[var(--color-ink-secondary)]">
          {model.when}
        </p>

        {(model.clientName !== null || model.projectName !== null) && (
          <p className="mt-1 truncate text-[12px] text-[var(--color-ink-muted)]">
            {[model.clientName, model.projectName].filter((part) => part !== null).join(" · ")}
          </p>
        )}

        {isGoogle && (model.attendees?.length ?? 0) > 0 && (
          <p className="mt-1 truncate text-[11px] text-[var(--color-ink-muted)]">
            {(model.attendees ?? []).length === 1
              ? model.attendees?.[0]
              : `${(model.attendees ?? []).slice(0, 2).join(", ")}${(model.attendees?.length ?? 0) > 2 ? ` +${(model.attendees?.length ?? 0) - 2}` : ""}`}
          </p>
        )}

        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {isGoogle ? (
            <>
              <span className="inline-flex h-5 items-center rounded-full bg-[var(--color-surface-muted)] px-2 text-[0.6875rem] font-medium text-[var(--color-ink-muted)]">
                Agenda
              </span>
              {model.hangoutLink !== null &&
              model.hangoutLink !== undefined &&
              model.hangoutLink !== "" ? (
                <span className="inline-flex h-5 items-center rounded-full bg-[#E8F0FE] px-2 text-[0.6875rem] font-medium text-[#1967D2]">
                  Meet
                </span>
              ) : null}
              <span className="inline-flex h-5 items-center rounded-full bg-[var(--color-surface-muted)] px-2 text-[0.6875rem] font-medium text-[var(--color-ink-muted)]">
                Interna
              </span>
            </>
          ) : (
            <>
              <StatusBadge status={model.status} />
              {model.isLate && <LateBadge />}
              {model.isRecurring && <RecurringBadge />}
              {model.hasPublish && <PublishBadge />}
              {!model.visibleToClient && (
                <span className="inline-flex h-5 items-center rounded-full bg-[var(--color-surface-muted)] px-2 text-[0.6875rem] font-medium text-[var(--color-ink-muted)]">
                  Interna
                </span>
              )}
            </>
          )}
        </div>

        {snippet !== null && (
          <p className="mt-2 line-clamp-2 text-[12px] leading-4 text-[var(--color-ink-secondary)]">
            {snippet}
            {model.description !== null && model.description.trim().length > 140 ? "…" : ""}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
